// Eskom PARCC — the dashboard drill-through panel.
//
// Renders a {@link DrillModel} inside the shared `Flyout`, so it is a right-hand
// slide-in under Lumen and a centred modal card under Legacy — same content,
// same actions, in both themes.
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Flyout, StatusIcon } from '../../components/ui';
import { formatRandCompact } from '../../lib/money';
import type { FacetState } from '../../domain/filters';
import { drillLink, type DrillModel, type DrillTone } from './drill';

const TONE_COLOUR: Record<DrillTone, string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  neutral: 'var(--muted)',
};

export function DrillPanel({
  model,
  facets,
  onClose,
}: {
  model: DrillModel | null;
  facets: FacetState;
  onClose: () => void;
}): React.JSX.Element | null {
  const navigate = useNavigate();
  if (!model) return null;

  function go(to: string): void {
    onClose();
    navigate(to);
  }

  return (
    <Flyout
      open
      onClose={onClose}
      title={model.title}
      subtitle={model.subtitle}
      width="narrow"
      headerExtra={
        model.stat ? (
          <div className="lm-flyout-stat tnum" style={{ color: TONE_COLOUR[model.tone] }}>
            {model.stat}
          </div>
        ) : undefined
      }
    >
      {model.breakdown.length > 0 && (
        <div
          style={{
            background: 'var(--panel-2)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div
            className="smallcaps"
            style={{ color: 'var(--muted)', fontWeight: 700, marginBottom: 12 }}
          >
            {model.breakdownTitle}
          </div>
          <div className="lm-bars">
            {model.breakdown.map((b) => (
              <div className="lm-bar" key={b.label}>
                <span className="lab" title={b.label}>
                  {b.label}
                </span>
                <span className="track">
                  <i style={{ width: `${b.pct}%` }} />
                </span>
                <span className="val">{b.display}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div className="smallcaps" style={{ color: 'var(--muted)', fontWeight: 700 }}>
          Transactions ({model.totalRows.toLocaleString('en-ZA')})
        </div>
        <span className="small">Click a row to open it</span>
      </div>

      {model.rows.length === 0 ? (
        <div className="empty">No transactions in this slice.</div>
      ) : (
        model.rows.map((tx) => (
          <button
            key={tx.id || tx.referenceNo}
            type="button"
            className="btn ghost"
            onClick={() => go(`/transactions?ref=${encodeURIComponent(tx.referenceNo)}`)}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: 11,
              alignItems: 'center',
              width: '100%',
              height: 'auto',
              textAlign: 'left',
              padding: '11px 13px',
              marginBottom: 8,
              justifyContent: 'stretch',
            }}
          >
            <StatusIcon value={tx.complianceLevel ?? 'Pending Review'} size={15} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span className="tnum" style={{ fontWeight: 700, fontSize: 12 }}>
                  {tx.referenceNo}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 12.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {tx.supplier ?? '—'}
                </span>
              </span>
              <span className="small" style={{ display: 'block', marginTop: 3 }}>
                {[tx.dataset, tx.division, tx.mechanism].filter(Boolean).join(' · ') || '—'}
              </span>
            </span>
            <span className="tnum small" style={{ whiteSpace: 'nowrap' }}>
              {formatRandCompact(tx.value)}
            </span>
          </button>
        ))
      )}

      <button
        type="button"
        className="btn primary"
        style={{ width: '100%', marginTop: 8 }}
        onClick={() => go(drillLink(model, facets))}
      >
        {model.ctaLabel} <ArrowRight size={15} aria-hidden />
      </button>
    </Flyout>
  );
}
